import { Dispatch, useEffect, useRef, useCallback } from 'react';
import { AppRouteAction, SearchItem } from '../types';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateQueryParams } from '../../utils/validateQueryParams';
import updateParams from '../../utils/updateParams';
import { fetchSearchItems, getFilterKey, splitIdents } from '../helpers/searchHelpers';
import { isValidCuratedSource } from '../../modules/search/searchGrid/helpers/sourceListHelpers';

interface UseUrlSyncProps {
  searchParams: URLSearchParams;
  hasValidParams: boolean;
  restrictSources: string | null;
  isLoading: boolean;
  dispatch: Dispatch<AppRouteAction>;
  setView: (view: string) => void;
  setSource: (source: string) => void;
  setTab: (tab: string) => void;
  setSearchItems: (items: SearchItem[]) => void;
  setHasValidParams: (valid: boolean) => void;
  setMappingContent: (content: any) => void;
  authorization: any;
  isAuthenticated: boolean;
  setAuthorization: (auth: any) => void;
  setIsAuthenticated: (auth: boolean) => void;
  setGroups: (groups: any) => void;
  setAuthError: (error: any) => void;
  searchItems: SearchItem[];
}

export const useUrlSync = ({
  searchParams,
  restrictSources,
  dispatch,
  setView,
  setSource,
  setTab,
  setSearchItems,
  setHasValidParams,
  authorization,
  isAuthenticated,
  setAuthorization,
  setIsAuthenticated,
  setGroups,
  setAuthError,
  searchItems,
}: UseUrlSyncProps) => {
  const navigate = useNavigate();
  const { pathname: routerPathname, search: routerSearch } = useLocation();
  const processingRef = useRef(false);
  const lastProcessedParamsRef = useRef<string>('');

  const handleSearchItemsUpdate = useCallback(
    async (newIdents: string, searchType: string) => {
      const selectedFilter = getFilterKey(searchType);
      const inputs = splitIdents(newIdents, searchType);

      dispatch({ type: 'SET_IS_DATA_READY', payload: false });

      try {
        const newSearchItems = await fetchSearchItems({
          validInputs: inputs,
          searchType,
          selectedFilter,
          authorization,
          isAuthenticated,
          setAuthorization,
          setIsAuthenticated,
          setGroups,
          setAuthError,
        });

        setSearchItems(newSearchItems);
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_IS_DATA_READY', payload: true });
      } catch (error: unknown) {
        console.error('Error fetching search items:', error);
        dispatch({ type: 'SET_IS_DATA_READY', payload: false });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [
      authorization,
      isAuthenticated,
      setAuthorization,
      setIsAuthenticated,
      setGroups,
      setAuthError,
      setSearchItems,
      dispatch,
    ],
  );

  useEffect(() => {
    if (processingRef.current) return;

    const currentParams = `${routerPathname}${routerSearch}`;
    if (lastProcessedParamsRef.current === currentParams) return;

    processingRef.current = true;
    lastProcessedParamsRef.current = currentParams;

    dispatch({ type: 'RESET_STATE' });

    if (routerPathname === '/') {
      setView('');
      setSource('');
      setTab('');
      setSearchItems([]);
      processingRef.current = false;
      return;
    }

    const hasSearchParams = searchParams.toString() !== '';

    if (hasSearchParams && ['/examples', '/search'].includes(routerPathname)) {
      const newView = searchParams.get('view');
      const newSource = searchParams.get('source');
      const newTab = searchParams.get('tab');
      const newIdents = searchParams.get('idents');

      const isValidParams = validateQueryParams(searchParams);

      if (!isValidParams) {
        setHasValidParams(false);
        dispatch({ type: 'SET_HAS_VALID_PARAMS', payload: false });
        processingRef.current = false;
        navigate('/Error');
        return;
      }

      setHasValidParams(true);
      dispatch({ type: 'SET_HAS_VALID_PARAMS', payload: true });

      setView(newView as string);
      setSource(newSource as string);
      setTab(newTab as string);

      if (restrictSources === 'CURATED' && newSource !== 'CURATED') {
        if (!isValidCuratedSource(newSource, newTab)) {
          processingRef.current = false;
          navigate({
            pathname: routerPathname,
            search: updateParams({ source: 'CURATED' }),
          });
          return;
        }
      }

      if (newIdents === 'ALL') {
        setSearchItems([]);
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_IS_DATA_READY', payload: true });
        processingRef.current = false;
        return;
      }

      const prevUrlIdents = searchItems.map(item => item.id).join('-');
      if (newIdents && prevUrlIdents === newIdents) {
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_IS_DATA_READY', payload: true });
        processingRef.current = false;
        return;
      }

      if (newIdents && newView) {
        dispatch({ type: 'SET_LOADING', payload: true });
        handleSearchItemsUpdate(newIdents, newView).finally(() => {
          processingRef.current = false;
          // Ensure loading is turned off even if handleSearchItemsUpdate doesn't do it
          dispatch({ type: 'SET_LOADING', payload: false });
        });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        processingRef.current = false;
      }
    } else if (['/examples', '/search'].includes(routerPathname) && !hasSearchParams) {
      processingRef.current = false;
      const defaultSource = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      navigate(
        {
          pathname: routerPathname,
          search: `?view=ALL&idents=ALL&source=${defaultSource}&tab=GDA`,
        },
        { replace: true },
      );
      return;
    } else {
      processingRef.current = false;
    }
  }, [
    searchParams,
    routerPathname,
    routerSearch,
    restrictSources,
    searchItems,
    navigate,
    setView,
    setSource,
    setTab,
    setSearchItems,
    setHasValidParams,
    dispatch,
    handleSearchItemsUpdate,
  ]);

  useEffect(() => {
    return () => {
      processingRef.current = false;
    };
  }, []);
};
